import { useState } from "react";
import type { LightingTechnique } from "@/mocks/gallery";

interface TechniqueGridProps {
  techniques: LightingTechnique[];
}

/**
 * A quiet, technical grid of the lighting techniques used in the project.
 * Hover expands each card with a warm underline that draws itself.
 */
export default function TechniqueGrid({ techniques }: TechniqueGridProps) {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  return (
    <section className="relative py-24 md:py-40 px-6 md:px-14 border-t border-white/10 bg-[#0a0a0a]">
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-16 md:mb-20">
          <h2
            className="font-black uppercase leading-[0.95] tracking-[-0.02em] text-white"
            style={{ fontSize: "clamp(32px, 4.4vw, 68px)" }}
          >
            Techniques
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {techniques.map((t, i) => {
            const isActive = activeIdx === i;
            return (
              <div
                key={t.code}
                className="relative p-8 border border-white/10 rounded-lg bg-white/[0.02] cursor-pointer overflow-hidden group transition-colors duration-500 hover:bg-white/[0.05]"
                onMouseEnter={() => setActiveIdx(i)}
                onMouseLeave={() => setActiveIdx(null)}
              >
                {/* Warm underline that draws itself on hover */}
                <span
                  className="absolute left-0 bottom-0 h-[2px] bg-gradient-to-r from-[#ff9a55] via-[#ffb56b] to-transparent transition-all duration-700"
                  style={{ width: isActive ? "100%" : "0%" }}
                />

                <div className="flex items-center justify-between mb-8">
                  <span className="text-[10px] uppercase tracking-[0.4em] text-white/50 font-mono">
                    {t.code}
                  </span>
                  <span
                    className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center transition-all duration-500"
                    style={{
                      borderColor: isActive
                        ? "rgba(255, 180, 110, 0.7)"
                        : "rgba(255,255,255,0.2)",
                      backgroundColor: isActive
                        ? "rgba(255, 180, 110, 0.12)"
                        : "transparent",
                    }}
                  >
                    <i className="ri-lightbulb-flash-line text-white/80" />
                  </span>
                </div>

                <h3
                  className="font-black uppercase leading-tight text-white mb-4"
                  style={{ fontSize: "clamp(20px, 1.7vw, 26px)" }}
                >
                  {t.title}
                </h3>

                <p className="text-white/65 text-sm leading-relaxed mb-6">
                  {t.detail}
                </p>

                <div className="text-[10px] uppercase tracking-[0.35em] text-white/50 font-mono border-t border-white/10 pt-4">
                  {t.spec}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}